import { useCallback, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  DragHandleDots2Icon,
  EyeNoneIcon,
  EyeOpenIcon
} from '@radix-ui/react-icons';
import { Flex, Text } from '@raystack/apsara';
import clsx from 'clsx';
import _ from 'lodash';

import { AssetTextWrapper } from '@src/components/Custom/ModelCommonComponents/CDCommonComponents/AssetTextWrapper';
import OverlaySlider from '@src/components/Custom/ModelCommonComponents/CDCommonComponents/OverlaySlider';
import styles from '@src/components/Custom/ModelCommonComponents/CDCommonComponents/styles.module.css';
import { getDateForAsset } from '@src/components/Custom/ModelCommonComponents/ChangeDetectionCommon';
import { SquareThumbnail } from '@src/components/Custom/Thumbnails';
import {
  IndiceDict,
  getPresetIndiceTitle
} from '@src/containers/Viewer/subComponents/Insights/Pages/Outputs/ChangeDetectionOutput';
import { Show } from '@src/hoc';
import { useGetSatelliteById } from '@src/hooks';
import { useGetBlockDetails } from '@src/hooks/useBlocksHooks';
import {
  InputRasterThumbnailHandler,
  InputRasterUrlHandler,
  getCogURLIndices
} from '@src/utils/MapUtils';
import { ASSET_TYPE_STRINGS } from '@src/utils/constants/ATLAS_CONSTANTS';
import { ImageLinks } from '@src/utils/constants/MODE_INFERENCE_DEFAULTS';
import { getUSTimeFormat } from '@src/utils/dateUtils';
import { getCogURLForOutput, isDiscrete } from '@src/utils/workFlowUtils';

import RasterColorInfo from './RasterColorInfo';

const LAYER_TYPES = {
  MODEL: 'model',
  SCENE: 'scene',
  VIZ: 'viz'
};

const ImageLayer = ({ id, layer, onOpacityChange, toggleLayerVisiblity }) => {
  const {
    src,
    layer_name,
    description,
    asset,
    layerType,
    isDiscretised = false,
    blockName = ''
  } = layer;
  const [expand, setExpand] = useState(false);

  const isModel = layerType === LAYER_TYPES.MODEL;

  // fetch block details
  const { data: blockData, isLoading } = useGetBlockDetails({
    blockName,
    onError: () => {}
  });

  let name = layer_name;
  const {
    isSatelliteLoading: satelliteLoading,
    satelliteData: { name: satellite_name } = {}
  } = useGetSatelliteById({
    id: layerType === LAYER_TYPES.SCENE ? name : null
  });

  if (layerType === LAYER_TYPES.SCENE) {
    name = satelliteLoading ? (
      <Skeleton height={10} width={100} />
    ) : (
      satellite_name
    );
  }

  const getBlockTitle = useCallback(
    (blockData, blockName, isDiscrete) => {
      const createLayerTitle = name => (isDiscrete ? `${name} Class` : name);
      const title = createLayerTitle(blockData?.spec?.title || blockName);

      return isLoading ? <Skeleton width='50px' /> : title;
    },
    [isLoading]
  );

  const {
    attributes,
    listeners,
    isDragging,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  useEffect(() => {
    if (isDragging) setExpand(false);
  }, [isDragging]);

  return (
    <Flex
      direction='column'
      gap='medium'
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={clsx(styles.layerItemWrapper, {
        [styles.disabled]: !layer.visible
      })}
      data-dragging={isDragging}
    >
      <Flex align='start' justify='between' className={styles.layerItem}>
        <Flex gap='small' align='start'>
          <DragHandleDots2Icon {...listeners} className={styles.dragIcon} />
          <Flex gap='small' align='center'>
            <SquareThumbnail src={src} />
            <AssetTextWrapper
              title={
                isModel
                  ? getBlockTitle(blockData, blockName, isDiscretised)
                  : name
              }
              date={description}
              asset={asset}
            />
          </Flex>
        </Flex>

        <Flex align='center' gap='small'>
          <Show
            when={layer.visible}
            fallback={
              <EyeNoneIcon
                className={clsx(styles.pointer, styles.hideIcon)}
                color='var(--rs-color-foreground-base-tertiary)'
                data-test-id='hide-layer'
                onClick={() => {
                  toggleLayerVisiblity(id);
                }}
              />
            }
          >
            <EyeOpenIcon
              className={clsx(styles.pointer, styles.hideIcon)}
              color='var(--rs-color-foreground-base-tertiary)'
              data-test-id='show-layer'
              onClick={() => {
                toggleLayerVisiblity(id);
              }}
            />
          </Show>

          <Show
            when={expand}
            fallback={
              <ChevronRightIcon
                className={styles.pointer}
                color='var(--rs-color-foreground-base-tertiary)'
                data-test-id='expand-layer-info'
                onClick={() => setExpand(true)}
              />
            }
          >
            <ChevronDownIcon
              className={styles.pointer}
              data-test-id='hide-layer-info'
              color='var(--rs-color-foreground-base-tertiary)'
              onClick={() => setExpand(false)}
            />
          </Show>
        </Flex>
      </Flex>
      <Show when={expand}>
        <Flex direction='column' gap='medium'>
          <Flex direction='column' gap='small'>
            <Text size={1} weight={400}>
              Layer Opacity:
            </Text>
            <OverlaySlider
              title=''
              value={layer.opacity}
              onChange={value =>
                onOpacityChange({
                  id: layer.id,
                  opacity: value
                })
              }
              data-test-id={`${layer.id}-opacity-slider`}
            />
          </Flex>
          <RasterColorInfo layer={layer} />
        </Flex>
      </Show>
    </Flex>
  );
};

const filterForNonDiscreteSource = (layers = []) => {
  const findInputRefAsset = layers.find(l => l.name === 'raster_url_ref') || {};
  const { properties = {} } = findInputRefAsset;
  const isDiscreteLayer = isDiscrete(properties);

  return isDiscreteLayer
    ? layers.filter(i => !['raster_ref', 'raster_comp'].includes(i.name))
    : layers;
};

const getLayerOrdering = ({
  ReferenceLayer,
  ComparisonLayer,
  DiscretisedReferenceLayer,
  DiscritisedComparisonLayer,
  ReferenceScene,
  ComparisonScene
}) => {
  // required order of comparison layers based on PRD
  let orderLayers = [];

  if (ReferenceScene && ComparisonScene) {
    orderLayers = [
      ReferenceLayer,
      DiscretisedReferenceLayer,
      ReferenceScene,
      ComparisonLayer,
      DiscritisedComparisonLayer,
      ComparisonScene
    ];
    return [...orderLayers.map((d, i) => ({ ...d, id: `image-${i}` }))];
  }

  // scenes are excluded from this for workflow support
  orderLayers = [
    ReferenceLayer,
    DiscretisedReferenceLayer,
    ComparisonLayer,
    DiscritisedComparisonLayer
  ];

  return [...orderLayers.map((d, i) => ({ ...d, id: `image-${i}` }))];
};

// TODO: to be explored in the future
// const checkIsDiscrete = inputOutputSpec => {
//   const { type = '' } = inputOutputSpec?.properties?.visualisation || {};

//   // case where dexter output does not have visualisation property
//   // or a non dexter asset has been passed
//   if (type === '') return false;

//   if (type === COLORMAP_TYPES.discreteColormap) return true;

//   return false;
// };

const getInputUrlAndType = (inputAsset = {}) => {
  const url =
    inputAsset?.stac_url?.length > 0 ? inputAsset?.stac_url : inputAsset?.value;
  const assetType =
    inputAsset?.stac_url?.length > 0
      ? ASSET_TYPE_STRINGS.stac_item
      : ASSET_TYPE_STRINGS.mosaic;
  return { url, assetType };
};

export const getImageLayers = args => {
  const {
    inputs,
    outputs,
    date_ref,
    date_comp,
    isModel,
    asset_type,
    format,
    activeAoi,
    insights,
    visualisations = []
  } = args;

  const selectedVisualisationAssets = visualisations[asset_type] || [];
  // getting both ref and comp I/O from args
  const inputRef = inputs.find(d => d.name === 'raster_url_ref');
  const outputRef = outputs.find(d => d.name === 'raster_ref');
  const inputComp = inputs.find(d => d.name === 'raster_url_comp');
  const outputComp = outputs.find(d => d.name === 'raster_comp');

  // ref related all assets
  // input ref
  const ReferenceLayer = {
    ...inputRef,
    asset: 'Reference',
    layer_name: isModel
      ? inputRef.metadata['block-name']
      : getPresetIndiceTitle(asset_type),
    blockName: inputRef?.metadata?.['block-name'] ?? '',
    description: getDateForAsset(inputRef.metadata['block-name'], date_ref),
    src: isModel
      ? ImageLinks[inputRef.metadata['block-name']]
      : IndiceDict[asset_type]?.icon,
    layerType: isModel ? LAYER_TYPES.MODEL : LAYER_TYPES.VIZ,
    isDiscretised: false,
    opacity: [1],
    visible: true,
    // to handle colormap rendering in UI for visualisation assets
    indice_id: !isModel ? asset_type : '',
    url: isModel
      ? getCogURLForOutput(inputRef, format)
      : getCogURLIndices(
          inputRef?.value || '',
          asset_type,
          format,
          selectedVisualisationAssets
        )
  };
  // output ref
  const DiscretisedReferenceLayer = {
    ...outputRef,
    asset: 'Reference',
    layer_name: `${isModel ? inputRef.metadata['block-name'] : asset_type} class`,
    blockName: inputRef?.metadata?.['block-name'] ?? '',
    description: getDateForAsset(inputRef.metadata['block-name'], date_ref),
    src: isModel
      ? ImageLinks[inputRef.metadata['block-name']]
      : IndiceDict[asset_type]?.icon,
    layerType: isModel ? LAYER_TYPES.MODEL : LAYER_TYPES.VIZ,
    isDiscretised: true,
    opacity: [1],
    visible: true,
    url: getCogURLForOutput(outputRef, format)
  };

  // comp related assets
  // input comp
  const ComparisonLayer = {
    ...inputComp,
    asset: 'Comparison',
    layer_name: isModel
      ? inputComp.metadata['block-name']
      : getPresetIndiceTitle(asset_type),
    blockName: inputComp?.metadata?.['block-name'] ?? '',
    description: getDateForAsset(inputComp.metadata['block-name'], date_comp),
    src: isModel
      ? ImageLinks[inputComp.metadata['block-name']]
      : IndiceDict[asset_type]?.icon,
    layerType: isModel ? LAYER_TYPES.MODEL : LAYER_TYPES.VIZ,
    isDiscretised: false,
    opacity: [1],
    visible: true,
    // to handle colormap rendering in UI for visualisation assets
    indice_id: !isModel ? asset_type : '',
    url: isModel
      ? getCogURLForOutput(inputComp, format)
      : getCogURLIndices(
          inputComp?.value || '',
          asset_type,
          format,
          selectedVisualisationAssets
        )
  };
  // output comp
  const DiscritisedComparisonLayer = {
    ...outputComp,
    asset: 'Comparison',
    layer_name: `${isModel ? inputComp.metadata['block-name'] : asset_type} class`,
    blockName: inputComp?.metadata?.['block-name'] ?? '',
    description: getDateForAsset(inputComp.metadata['block-name'], date_comp),
    src: isModel
      ? ImageLinks[inputComp.metadata['block-name']]
      : IndiceDict[asset_type]?.icon,
    layerType: isModel ? LAYER_TYPES.MODEL : LAYER_TYPES.VIZ,
    isDiscretised: true,
    opacity: [1],
    visible: true,
    url: getCogURLForOutput(outputComp, format)
  };

  // handle scenes
  // NOTE: we can get scene path from related insights of ref and comp
  // This is not applicable for Visualisation asset inputs
  // since it depends on dexter's input spec
  if (_.has(args, 'activeAoi') && _.has(args, 'insights') && isModel) {
    // get related inputRef/Comp Assets from their insights
    const inputRefAsset = insights?.find(
      insight => insight.id === inputRef.asset_source.id
    )?.input?.[0];
    const inputCompAsset = insights?.find(
      insight => insight.id === inputComp.asset_source.id
    )?.input?.[0];

    const { url: inputRefUrl, assetType: inputRefType } =
      getInputUrlAndType(inputRefAsset);
    const { url: inputCompUrl, assetType: inputCompType } =
      getInputUrlAndType(inputCompAsset);

    const ReferenceScene = {
      asset: 'Reference',
      layer_name: activeAoi.lens_satellite_id || activeAoi.satellite_name,
      description: getUSTimeFormat(inputRefAsset.properties.date),
      src: InputRasterThumbnailHandler({
        activeAoi,
        url: inputRefUrl,
        assetType: inputRefType
      }),
      url: InputRasterUrlHandler({
        activeAoi,
        format,
        url: inputRefUrl,
        assetType: inputRefType
      }),
      layerType: LAYER_TYPES.SCENE,
      opacity: [1],
      visible: true
    };

    const ComparisonScene = {
      asset: 'Comparison',
      layer_name: activeAoi.lens_satellite_id || activeAoi.satellite_name,
      description: getUSTimeFormat(inputCompAsset.properties.date),
      src: InputRasterThumbnailHandler({
        activeAoi,
        url: inputCompUrl,
        assetType: inputCompType
      }),
      url: InputRasterUrlHandler({
        activeAoi,
        format,
        url: inputCompUrl,
        assetType: inputCompType
      }),
      layerType: LAYER_TYPES.SCENE,
      opacity: [1],
      visible: true
    };

    return filterForNonDiscreteSource([
      ...getLayerOrdering({
        ReferenceLayer,
        ComparisonLayer,
        DiscretisedReferenceLayer,
        DiscritisedComparisonLayer,
        ReferenceScene,
        ComparisonScene
      })
    ]);
  }

  return filterForNonDiscreteSource([
    ...getLayerOrdering({
      ReferenceLayer,
      ComparisonLayer,
      DiscretisedReferenceLayer,
      DiscritisedComparisonLayer
    })
  ]);
};

const ImageLayerList = ({ layers, onLayersChange, layersLoading }) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  if (layersLoading)
    return (
      <Flex gap='medium'>
        <Skeleton width='60px' height='60px' />
        <Flex direction='column' gap='small' align='start'>
          <Skeleton width='200px' />
          <Skeleton width='200px' />
        </Flex>
      </Flex>
    );

  const layerMove = (items, active, over) => {
    const oldIndex = items.findIndex(item => item.id === active.id);
    const newIndex = items.findIndex(item => item.id === over.id);
    return arrayMove(items, oldIndex, newIndex);
  };

  function handleDragEnd(event) {
    const { active, over } = event;
    if (active.id !== over.id) {
      const updatedLayers = layerMove(layers, active, over);
      onLayersChange(updatedLayers);
    }
  }

  const onOpacityChange = ({ id, opacity }) => {
    onLayersChange([
      ...layers.map(layer => {
        if (layer.id === id) return { ...layer, opacity };
        return layer;
      })
    ]);
  };

  const toggleLayerVisiblity = id => {
    const updatedLayers = layers.map(layer => {
      if (layer.id === id) return { ...layer, visible: !layer.visible };
      return layer;
    });
    const layerToMove = updatedLayers.find(layer => !layer.visible);
    if (layerToMove)
      onLayersChange([
        ...updatedLayers
          .filter(d => d.id !== layerToMove?.id)
          .sort((a, b) => b.visible - a.visible),
        layerToMove
      ]);
    else onLayersChange(updatedLayers);
  };

  return (
    <Flex gap='medium' direction='column' className={styles.layersSection}>
      <Text size={4}>Layers</Text>
      <div style={{ width: '100%', position: 'relative' }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={layers}
            strategy={verticalListSortingStrategy}
          >
            {layers.map(layer => (
              <ImageLayer
                key={layer.id}
                id={layer.id}
                layer={layer}
                onOpacityChange={onOpacityChange}
                toggleLayerVisiblity={toggleLayerVisiblity}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </Flex>
  );
};

export default ImageLayerList;
